import { Box, Tooltip, Typography, alpha } from "@mui/material";

import { StatusIndicator, type StatusTone } from "@components/StatusIndicator";

import type { Priority, RequirementStatus } from "../types";

/* ── ID badge (mono) ──────────────────────────────────────────────────────
 *
 * The user-facing public id (`REQ-PWR-003`) rendered as a compact mono
 * pill. Same vocabulary as version chips and refdes labels elsewhere
 * in the app — JetBrains Mono with tabular numerals.
 */

export function RequirementId({ id }: { id: string }) {
  return (
    <Typography
      component="span"
      sx={(t) => ({
        fontFamily: t.fonts?.mono ?? "monospace",
        fontSize: "0.6875rem",
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "0.04em",
        color: t.palette.text.secondary,
        background: alpha(t.palette.text.primary, 0.04),
        border: `1px solid ${t.palette.border?.default ?? t.palette.divider}`,
        borderRadius: 1,
        padding: "2px 6px",
        flexShrink: 0,
        whiteSpace: "nowrap",
      })}
    >
      {id}
    </Typography>
  );
}

/* ── Priority badge ───────────────────────────────────────────────────────
 *
 * Single-glyph mono badge — M / S / C / W. Must reads loudest (editorial
 * amber); Should is neutral; Could/Won't fade. Same square 8px radius as
 * every other chip in the app — no oval pills.
 */

const PRIORITY_GLYPH: Record<Priority, string> = {
  must: "M",
  should: "S",
  could: "C",
  wont: "W",
};

const PRIORITY_LABEL: Record<Priority, string> = {
  must: "Must",
  should: "Should",
  could: "Could",
  wont: "Won't",
};

export function PriorityBadge({ priority }: { priority: Priority }) {
  return (
    <Tooltip title={PRIORITY_LABEL[priority]} arrow placement="top">
      <Box
        component="span"
        sx={(t) => {
          const base = {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            borderRadius: 1,
            fontFamily: t.fonts?.mono ?? "monospace",
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.02em",
            lineHeight: 1,
            flexShrink: 0,
          } as const;
          switch (priority) {
            case "must":
              return {
                ...base,
                background: alpha(t.palette.editorial?.main ?? "#d4a05c", 0.16),
                color: t.palette.editorial?.contrastText ?? "#f0c794",
                border: `1px solid ${alpha(
                  t.palette.editorial?.main ?? "#d4a05c",
                  0.4,
                )}`,
              };
            case "should":
              return {
                ...base,
                background: alpha(t.palette.text.primary, 0.06),
                color: t.palette.text.primary,
                border: `1px solid ${
                  t.palette.border?.default ?? t.palette.divider
                }`,
              };
            case "could":
              return {
                ...base,
                background: "transparent",
                color: t.palette.text.secondary,
                border: `1px solid ${
                  t.palette.border?.default ?? t.palette.divider
                }`,
              };
            case "wont":
              return {
                ...base,
                background: "transparent",
                color: t.palette.text.disabled,
                border: `1px dashed ${alpha(t.palette.text.disabled, 0.4)}`,
              };
          }
        }}
      >
        {PRIORITY_GLYPH[priority]}
      </Box>
    </Tooltip>
  );
}

/* ── Status dot ───────────────────────────────────────────────────────────
 *
 * Same vocabulary as the global `StatusDot` component. Status is now
 * carried by `StatusIndicator`, so each state has a distinct glyph and
 * an accessible name in addition to its colour — it's no longer told
 * apart by colour alone (WCAG 1.4.1).
 */

const STATUS_TONE: Record<RequirementStatus, StatusTone> = {
  suggested: "warning",
  draft: "neutral",
  review: "warning",
  approved: "active",
  verified: "success",
};

const STATUS_LABEL: Record<RequirementStatus, string> = {
  suggested: "Suggested · awaiting review",
  draft: "Draft",
  review: "In review",
  approved: "Approved",
  verified: "Verified",
};

export function StatusDot({ status }: { status: RequirementStatus }) {
  return (
    <StatusIndicator tone={STATUS_TONE[status]} label={STATUS_LABEL[status]} />
  );
}
